import { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Tab, Tabs } from 'react-bootstrap';
import type {
  TierDefinition,
  UpdateTierDefinitionRequest,
  TierBenefits,
} from '../../../../infrastructure/LoyaltyClient';

interface UpdateTierDefinitionModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (request: UpdateTierDefinitionRequest) => Promise<void>;
  isSubmitting: boolean;
  tier: TierDefinition;
}

export const UpdateTierDefinitionModal = ({
  show,
  onHide,
  onSubmit,
  isSubmitting,
  tier,
}: UpdateTierDefinitionModalProps) => {
  const [level, setLevel] = useState('');
  const [minPoints, setMinPoints] = useState('');
  const [pointMultiplier, setPointMultiplier] = useState('');
  const [description, setDescription] = useState('');

  const [redemptionEnabled, setRedemptionEnabled] = useState(false);
  const [specialDiscounts, setSpecialDiscounts] = useState(false);
  const [customBenefits, setCustomBenefits] = useState('');

  const [color, setColor] = useState('#6c757d');
  const [icon, setIcon] = useState('');
  const [displayOrder, setDisplayOrder] = useState('');

  useEffect(() => {
    if (tier) {
      setLevel(tier.level?.toString() || tier.rank?.toString() || '');
      setMinPoints(tier.minPoints.toString());
      setPointMultiplier((tier.pointMultiplier || tier.multiplier || 1).toString());
      setDescription(tier.description || '');

      if (tier.displaySettings) {
        setColor(tier.displaySettings.color);
        setIcon(tier.displaySettings.icon);
        setDisplayOrder(tier.displaySettings.displayOrder.toString());
      }

      if (typeof tier.benefits === 'string') {
        setCustomBenefits(tier.benefits);
      } else if (Array.isArray(tier.benefits)) {
        setCustomBenefits(tier.benefits.join('\n'));
      } else if (typeof tier.benefits === 'object' && tier.benefits !== null) {
        const benefitsObj = tier.benefits as TierBenefits;
        setRedemptionEnabled(benefitsObj.redemptionEnabled || false);
        setSpecialDiscounts(benefitsObj.specialDiscounts || false);
        setCustomBenefits(benefitsObj.customBenefits || '');
      }
    }
  }, [tier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const request: UpdateTierDefinitionRequest = {};

    if (level) request.level = parseInt(level);
    if (minPoints) request.minPoints = parseInt(minPoints);
    if (pointMultiplier) request.pointMultiplier = parseFloat(pointMultiplier);
    if (description) request.description = description;

    if (customBenefits || redemptionEnabled || specialDiscounts) {
      request.benefits = {
        redemptionEnabled,
        specialDiscounts,
        customBenefits: customBenefits || '',
      };
    }

    if (color && icon && displayOrder) {
      request.displaySettings = {
        color,
        icon,
        displayOrder: parseInt(displayOrder),
      };
    }

    await onSubmit(request);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop={isSubmitting ? 'static' : true}>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Update Tier: {tier.tier}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs defaultActiveKey="basic" className="mb-3">
            <Tab eventKey="basic" title="Basic Settings">
              <Form.Group className="mb-3">
                <Form.Label>Level</Form.Label>
                <Form.Control
                  type="number"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  min="1"
                  step="1"
                  disabled={isSubmitting}
                  placeholder="Tier level (numerical priority)"
                />
                <Form.Text className="text-muted">Numerical priority for tier ordering</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Minimum Points</Form.Label>
                <Form.Control
                  type="number"
                  value={minPoints}
                  onChange={(e) => setMinPoints(e.target.value)}
                  min="0"
                  step="1"
                  disabled={isSubmitting}
                />
                <Form.Text className="text-muted">Minimum points required for this tier</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Point Multiplier</Form.Label>
                <Form.Control
                  type="number"
                  value={pointMultiplier}
                  onChange={(e) => setPointMultiplier(e.target.value)}
                  min="0.1"
                  max="10"
                  step="0.1"
                  disabled={isSubmitting}
                />
                <Form.Text className="text-muted">Point accrual multiplier (0.1 - 10)</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Tier description"
                />
              </Form.Group>
            </Tab>

            <Tab eventKey="benefits" title="Benefits">
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Redemption Enabled"
                  checked={redemptionEnabled}
                  onChange={(e) => setRedemptionEnabled(e.target.checked)}
                  disabled={isSubmitting}
                />
                <Form.Text className="text-muted">Allow customers to redeem points</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Special Discounts"
                  checked={specialDiscounts}
                  onChange={(e) => setSpecialDiscounts(e.target.checked)}
                  disabled={isSubmitting}
                />
                <Form.Text className="text-muted">Provide special discounts for this tier</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Custom Benefits</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={customBenefits}
                  onChange={(e) => setCustomBenefits(e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Enter custom benefits description"
                />
              </Form.Group>
            </Tab>

            <Tab eventKey="display" title="Display Settings">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Color</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        disabled={isSubmitting}
                        style={{ width: '60px' }}
                      />
                      <Form.Control
                        type="text"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="#FFD700"
                      />
                    </div>
                    <Form.Text className="text-muted">Tier color (hex code)</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Icon/Badge</Form.Label>
                    <Form.Control
                      type="text"
                      value={icon}
                      onChange={(e) => setIcon(e.target.value)}
                      disabled={isSubmitting}
                      placeholder="⭐"
                    />
                    <Form.Text className="text-muted">Emoji or icon for tier</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  min="1"
                  step="1"
                  disabled={isSubmitting}
                  placeholder="1"
                />
                <Form.Text className="text-muted">Order in which to display tier</Form.Text>
              </Form.Group>

              {icon && color && (
                <div className="mt-3 p-3 border rounded">
                  <small className="text-muted d-block mb-2">Preview:</small>
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: '1.5rem' }}>{icon}</span>
                    <div
                      style={{
                        padding: '4px 12px',
                        backgroundColor: color,
                        borderRadius: '4px',
                        color: '#fff',
                      }}
                    >
                      {tier.tier}
                    </div>
                  </div>
                </div>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Tier'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

